import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line, Doughnut, Bar, Pie } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement,
  Title, Tooltip, Legend, Filler,
)

const PERCENT_LABEL = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const RENDERERS = { line: Line, doughnut: Doughnut, pie: Pie, bar: Bar }

/**
 * Instance plugin : au survol d'un segment, estompe les autres séries (baisse
 * leur opacité de dessin), sans muter les couleurs. `hover` mode = point → un
 * seul segment actif ; on mémorise son datasetIndex et on ne redessine que
 * lorsqu'il change (garde anti-boucle). Générique, activé par `fadeOnHover`.
 */
const fadeOnHoverPlugin = {
  id: 'fadeOnHover',
  afterEvent(chart, args) {
    const { type } = args.event
    if (type !== 'mousemove' && type !== 'mouseout') return
    const active = chart.getActiveElements()
    const hovered = (type === 'mouseout' || !active.length) ? null : active[0].datasetIndex
    if (chart.$fadeHovered === hovered) return
    chart.$fadeHovered = hovered
    chart.update('none')
  },
  beforeDatasetDraw(chart, args) {
    const hovered = chart.$fadeHovered
    chart.ctx.save()
    chart.ctx.globalAlpha = (hovered == null || hovered === args.index) ? 1 : 0.2
  },
  afterDatasetDraw(chart) {
    chart.ctx.restore()
  },
}

/**
 * Instance plugin : écrit le TOTAL de chaque pile au-dessus de la barre
 * (histogramme empilé). Chart.js n'a pas de « datalabels » natif et une
 * fonction de dessin ne peut pas vivre en YAML — d'où ce plugin générique,
 * activé par le booléen déclaratif `stackTotals`. Il additionne les séries
 * VISIBLES (il suit donc les bascules de légende) et se cale sur le haut de la
 * barre la plus haute de l'index. Couleur et typo héritées du CSS du canvas
 * (design system) — aucune valeur codée en dur.
 */
const makeStackTotalsPlugin = (valueSuffix) => ({
  id: 'stackTotals',
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart
    if (chart.getDatasetMeta(0)?.type !== 'bar') return
    const count = data.labels?.length ?? 0
    const css = getComputedStyle(chart.canvas)
    ctx.save()
    ctx.font = `700 12px ${css.fontFamily || 'sans-serif'}`
    ctx.fillStyle = css.color || ChartJS.defaults.color
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    for (let i = 0; i < count; i += 1) {
      let total = 0
      let top = null
      let x = null
      data.datasets.forEach((ds, di) => {
        if (!chart.isDatasetVisible(di)) return
        const el = chart.getDatasetMeta(di).data[i]
        const v = Number(ds.data?.[i] ?? 0)
        if (!el || !Number.isFinite(v) || v === 0) return
        total += v
        if (top == null || el.y < top) { top = el.y; x = el.x }
      })
      if (top == null || total === 0) continue
      ctx.fillText(fmt(total, valueSuffix), x, top - 6)
    }
    ctx.restore()
  },
})

/**
 * Instance plugin : étiquette chaque part d'un camembert de son pourcentage et
 * de son effectif — « 55,56 % (20) » — posés À L'EXTÉRIEUR, reliés à la part par
 * une ligne de rappel coudée. Chart.js n'a pas de « datalabels » natif et un
 * tracé sur canvas ne peut pas vivre en YAML : d'où ce plugin générique, activé
 * par le booléen déclaratif `sliceLabels`.
 *
 * Les parts nulles sont ignorées (pas d'étiquette fantôme sur une part
 * invisible), et le total est celui des valeurs DESSINÉES, donc les pourcentages
 * somment bien à 100. Couleur et typo héritées du CSS du canvas.
 */
const makeSliceLabelsPlugin = (valueSuffix) => ({
  id: 'sliceLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const meta = chart.getDatasetMeta(0)
    if (!meta?.data?.length) return
    const values = (chart.data.datasets[0]?.data ?? []).map((v) => Number(v) || 0)
    const total = values.reduce((a, b) => a + b, 0)
    if (!total) return

    const css = getComputedStyle(chart.canvas)
    ctx.save()
    ctx.font = `600 11px ${css.fontFamily || 'sans-serif'}`
    ctx.fillStyle = css.color || ChartJS.defaults.color
    ctx.strokeStyle = ctx.fillStyle
    ctx.lineWidth = 1
    ctx.textBaseline = 'middle'

    meta.data.forEach((arc, i) => {
      const v = values[i]
      if (!v) return
      const { x, y, startAngle, endAngle, outerRadius } =
        arc.getProps(['x', 'y', 'startAngle', 'endAngle', 'outerRadius'], true)
      const mid = (startAngle + endAngle) / 2
      const cos = Math.cos(mid)
      const sin = Math.sin(mid)
      const toRight = cos >= 0
      // Ligne de rappel : du bord de la part vers l'extérieur, puis un coude
      // horizontal du côté où le texte se lit.
      const elbowX = x + cos * (outerRadius + 12)
      const elbowY = y + sin * (outerRadius + 12)
      const endX = elbowX + (toRight ? 12 : -12)
      ctx.beginPath()
      ctx.moveTo(x + cos * outerRadius, y + sin * outerRadius)
      ctx.lineTo(elbowX, elbowY)
      ctx.lineTo(endX, elbowY)
      ctx.stroke()

      ctx.textAlign = toRight ? 'left' : 'right'
      const pct = PERCENT_LABEL.format((v / total) * 100)
      ctx.fillText(`${pct} % (${fmt(v, valueSuffix)})`, endX + (toRight ? 4 : -4), elbowY)
    })
    ctx.restore()
  },
})

/**
 * Generic Chart.js renderer bound to a `{labels, datasets}` data object —
 * exactly the shape produced server-side / by a dataProcessor, so the YAML
 * stays declarative and a page never has to assemble datasets in JS.
 *
 * Chart.js options that need JS functions (number formatters, tooltip
 * callbacks) can't live in YAML, so they're driven here by simple declarative
 * props instead of raw option objects:
 *   - legendPosition   : 'bottom' | 'right' | 'top' | 'left' | 'none'
 *   - valueSuffix      : appended to Y ticks + tooltip values (e.g. ' €').
 *   - precisionZero    : integer-only Y ticks (no decimals).
 *   - beginAtZero      : force the Y axis to start at 0 (default true).
 *   - richTooltip      : tooltip « table » sur toute la colonne (mode index) —
 *                        trié décroissant, valeurs nulles masquées, ligne Total.
 *                        Évite d'avoir à relier une couleur à la légende.
 *   - seriesGroupLabel : suffixe ajouté à chaque ligne du tooltip, ex. le nom
 *                        du pipeline → « 1. Besoin détecté (Pipeline New Business) ».
  *   - fadeOnHover      : estompe les segments non survolés (highlight façon HubSpot).
 *   - stacked          : empile les séries (barres) — pose `stacked` sur X et Y.
 *   - xAxisTitle       : intitulé sous l'axe des abscisses (ex. « Date de création — Mensuel »).
 *   - yAxisTitle       : intitulé le long de l'axe des ordonnées (ex. « Nombre de transactions »).
 *   - stackTotals      : écrit le total de chaque pile au-dessus de la barre.
 *   - sliceLabels      : « 55,56 % (20) » à l'extérieur de chaque part d'un
 *                        camembert, relié par une ligne de rappel.
 * An optional `options` prop is shallow-merged last for any escape-hatch tweak.
 * ⚠️ Il REMPLACE la clé qu'il porte (merge de surface) : préférer les props
 * déclaratives ci-dessus, qui se composent dans `buildOptions`.
 *
 * YAML usage:
 *   - type: ChartJs
 *     chartType: bar
 *     data: ~~.pipeline.charts.byStageAmountPerMonth
 *     richTooltip: true
 *     fadeOnHover: true
 *     seriesGroupLabel: "Pipeline New Business"
 *     valueSuffix: " €"
 */
export const ChartJs = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const chartType = resolve(props.chartType) || 'line'
  const Renderer = RENDERERS[chartType] ?? Line
  const data = resolve(props.data) || { labels: [], datasets: [] }
  const legendPosition = resolve(props.legendPosition)
  const valueSuffix = resolve(props.valueSuffix)
  const precisionZero = resolve(props.precisionZero)
  const beginAtZero = resolve(props.beginAtZero)
  const richTooltip = resolve(props.richTooltip)
  const seriesGroupLabel = joinLabels(resolve(props.seriesGroupLabel))
  const fadeOnHover = resolve(props.fadeOnHover)
  const stacked = resolve(props.stacked)
  const xAxisTitle = resolve(props.xAxisTitle)
  const yAxisTitle = resolve(props.yAxisTitle)
  const stackTotals = resolve(props.stackTotals)
  const sliceLabels = resolve(props.sliceLabels)
  const optionsOverride = resolve(props.options)
  const wrapperClass = resolve(props.wrapperClass) ?? 'relative h-72'

  const options = buildOptions({
    chartType, legendPosition, valueSuffix, precisionZero, beginAtZero,
    richTooltip, seriesGroupLabel, fadeOnHover, stacked, xAxisTitle, yAxisTitle,
    stackTotals, sliceLabels,
  })
  const merged = optionsOverride && typeof optionsOverride === 'object'
    ? { ...options, ...optionsOverride }
    : options

  // Clic sur un segment. Chart.js ne donne que des INDEX (élément et série) :
  // c'est l'appelant qui sait ce qu'ils désignent, d'où un rappel à deux index
  // plutôt qu'un événement métier. `onClick` est posé APRÈS la fusion des
  // options pour qu'un `options:` de YAML ne le fasse pas disparaître en
  // silence, et le curseur suit — sans quoi rien n'indique que c'est cliquable.
  const onSegmentClick = props.onSegmentClick
  const finalOptions = typeof onSegmentClick === 'function'
    ? {
      ...merged,
      onClick: (_event, elements) => {
        const hit = elements?.[0]
        if (hit) onSegmentClick(hit.index, hit.datasetIndex)
      },
      onHover: (event, elements) => {
        const target = event?.native?.target
        if (target) target.style.cursor = elements?.length ? 'pointer' : 'default'
      },
    }
    : merged

  const instancePlugins = [
    ...(fadeOnHover ? [fadeOnHoverPlugin] : []),
    ...(stackTotals ? [makeStackTotalsPlugin(valueSuffix)] : []),
    ...(sliceLabels ? [makeSliceLabelsPlugin(valueSuffix)] : []),
  ]

  return (
    <ActionDependant {...props}>
      <div className={wrapperClass}>
        <Renderer data={data} options={finalOptions} plugins={instancePlugins} />
      </div>
    </ActionDependant>
  )
}

/** Formats a numeric value the French way, with an optional unit suffix. */
function fmt(value, suffix) {
  const n = value?.toLocaleString('fr-FR') ?? '—'
  return `${n}${suffix ?? ''}`
}

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
))

/**
 * External (HTML) tooltip handler for `richTooltip` : rend un tableau DOM (une
 * ligne par série, pastille + nom + valeur), **en gras la ligne survolée**, trié
 * décroissant, zéros masqués, ligne Total. Positionné du côté OPPOSÉ au curseur
 * pour ne pas recouvrir la barre lue. Réutilisable par n'importe quel chart.
 */
function makeExternalTooltip({ valueSuffix, seriesGroupLabel }) {
  return (context) => {
    const { chart, tooltip } = context
    const parent = chart.canvas.parentNode
    let el = parent.querySelector(':scope > .rj-chart-tooltip')
    if (!el) {
      el = document.createElement('div')
      el.className = 'rj-chart-tooltip'
      Object.assign(el.style, {
        position: 'absolute', pointerEvents: 'none', zIndex: '30',
        background: 'rgba(15,23,42,0.96)', color: '#fff', borderRadius: '10px',
        padding: '10px 12px', fontSize: '12px', lineHeight: '1.55',
        boxShadow: '0 10px 30px rgba(0,0,0,0.28)', maxWidth: '360px',
        transition: 'opacity 120ms ease', opacity: '0',
      })
      parent.appendChild(el)
    }
    if (tooltip.opacity === 0) { el.style.opacity = '0'; return }

    const hoveredDs = (chart.getActiveElements?.() || [])[0]?.datasetIndex ?? null
    const points = (tooltip.dataPoints || [])
      .filter((p) => (p.parsed.y ?? 0) > 0)
      .sort((a, b) => (b.parsed.y ?? 0) - (a.parsed.y ?? 0))
    const title = tooltip.title?.[0] ?? ''
    let total = 0
    const rows = points.map((p) => {
      total += p.parsed.y || 0
      const name = p.dataset.label ?? ''
      const full = seriesGroupLabel ? `${name} (${seriesGroupLabel})` : name
      const weight = p.datasetIndex === hoveredDs ? '700' : '400'
      return `<div style="display:flex;align-items:center;gap:6px;font-weight:${weight}">`
        + `<span style="width:10px;height:10px;border-radius:2px;flex:0 0 auto;background:${p.dataset.backgroundColor}"></span>`
        + `<span style="flex:1">${escapeHtml(full)}</span>`
        + `<span style="margin-left:14px;font-variant-numeric:tabular-nums">${escapeHtml(fmt(p.parsed.y, valueSuffix))}</span>`
        + `</div>`
    }).join('')
    el.innerHTML = `<div style="font-weight:600;margin-bottom:6px">${escapeHtml(title)}</div>${rows}`
      + `<div style="border-top:1px solid rgba(255,255,255,0.16);margin-top:6px;padding-top:6px;`
      + `font-weight:600;display:flex;justify-content:space-between;gap:14px">`
      + `<span>Total</span><span style="font-variant-numeric:tabular-nums">${escapeHtml(fmt(total, valueSuffix))}</span></div>`

    // Positionnement : côté opposé au curseur, clampé dans le canvas.
    const area = chart.chartArea
    const mid = (area.left + area.right) / 2
    const w = el.offsetWidth
    const h = el.offsetHeight
    let left = tooltip.caretX < mid ? tooltip.caretX + 22 : tooltip.caretX - w - 22
    left = Math.max(area.left, Math.min(left, chart.width - w - 4))
    const top = Math.max(4, Math.min(tooltip.caretY - h / 2, chart.height - h - 4))
    el.style.left = `${left}px`
    el.style.top = `${top}px`
    el.style.opacity = '1'
  }
}

/**
 * Aplati un libellé de groupe qui peut venir d'un filtre MULTI-valeurs : chaîne,
 * tableau de chaînes, ou tableau d'enveloppes `{ value, label }` (forme de
 * l'éditeur multi-sélection `es-select`). Un tableau vide rend `''` — un `[]`
 * étant truthy en JS, il afficherait sinon un suffixe « () » vide.
 */
function joinLabels(v) {
  if (!Array.isArray(v)) return v
  return v
    .map((e) => (e && typeof e === 'object' ? (e.label ?? e.value) : e))
    .filter((e) => e != null && e !== '')
    .join(', ')
}

/** Builds Chart.js options from the declarative props (no YAML-side functions). */
function buildOptions({
  chartType, legendPosition, valueSuffix, precisionZero, beginAtZero,
  richTooltip, seriesGroupLabel, fadeOnHover, stacked, xAxisTitle, yAxisTitle,
  stackTotals, sliceLabels,
}) {
  const base = { responsive: true, maintainAspectRatio: false }

  // Circular charts: only a legend knob, no cartesian axes.
  if (chartType === 'doughnut' || chartType === 'pie') {
    return {
      ...base,
      plugins: { legend: legendBlock(legendPosition ?? 'right') },
      // Les étiquettes de part se dessinent HORS du cercle : sans marge, elles
      // seraient rognées par le bord du canvas.
      ...(sliceLabels ? { layout: { padding: 56 } } : {}),
    }
  }

  const plugins = { legend: legendBlock(legendPosition ?? 'bottom') }

  if (richTooltip) {
    // Tooltip « tableau » HTML externe : ligne survolée en gras, positionné à
    // l'opposé du curseur (ne recouvre pas la barre lue). Voir makeExternalTooltip.
    plugins.tooltip = {
      enabled: false,
      mode: 'index',
      intersect: false,
      external: makeExternalTooltip({ valueSuffix, seriesGroupLabel }),
    }
  } else if (valueSuffix) {
    plugins.tooltip = {
      callbacks: { label: (c) => fmt(c.parsed.y, valueSuffix) },
    }
  }

  let ticks = {}
  if (valueSuffix) ticks = { callback: (v) => `${v}${valueSuffix}` }
  else if (precisionZero) ticks = { precision: 0 }

  // `fadeOnHover` veut un seul segment actif au survol (mode point) pour savoir
  // lequel garder net ; le tooltip reste en mode index (table), défini plus haut.
  const interaction = fadeOnHover
    ? { mode: 'point', intersect: true }
    : { mode: 'index', intersect: false }

  // Échelles composées ICI (et pas via `options`) : le merge de surface de
  // l'escape hatch remplacerait `scales` en bloc, et le formatage des ticks
  // (valueSuffix) serait perdu dès qu'un graphe est empilé.
  const x = { stacked: !!stacked }
  const y = { stacked: !!stacked, beginAtZero: beginAtZero !== false, ticks }
  if (xAxisTitle) x.title = { display: true, text: xAxisTitle }
  if (yAxisTitle) y.title = { display: true, text: yAxisTitle }

  return {
    ...base,
    interaction,
    plugins,
    scales: { x, y },
    // Le total d'une pile se dessine AU-DESSUS de la barre : sans marge haute
    // il serait rogné par le bord du canvas.
    ...(stackTotals ? { layout: { padding: { top: 20 } } } : {}),
  }
}

function legendBlock(position) {
  if (position === 'none') return { display: false }
  return { position, labels: { boxWidth: 12, usePointStyle: true } }
}
